import React, { useRef, useState } from 'react';
import { Link as RouterLink } from 'react-router';
import { Box, Button, Chip, Paper, Stack, Typography } from '@mui/material';
import { alpha, keyframes } from '@mui/material/styles';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import AssignmentTurnedInOutlinedIcon from '@mui/icons-material/AssignmentTurnedInOutlined';
import BadgeOutlinedIcon from '@mui/icons-material/BadgeOutlined';
import CalendarMonthOutlinedIcon from '@mui/icons-material/CalendarMonthOutlined';
import ChecklistRtlIcon from '@mui/icons-material/ChecklistRtl';
import ContactsOutlinedIcon from '@mui/icons-material/ContactsOutlined';
import EventOutlinedIcon from '@mui/icons-material/EventOutlined';
import QuestionAnswerOutlinedIcon from '@mui/icons-material/QuestionAnswerOutlined';
import SchoolOutlinedIcon from '@mui/icons-material/SchoolOutlined';
import WorkOutlineIcon from '@mui/icons-material/WorkOutline';
import useInView from '../useInView';
import { brand } from '../brand';

/**
 * The Career Wheel: the job seeker's whole search as one picture.
 *
 * The job is in the middle. Around it are the eight things that help a person get it, and around those a ring of
 * tasks that runs through all of them. It is on the home page to say, in a glance, what RoRi is for somebody
 * looking for work, and every part of it can be pressed to see what is in that part.
 *
 * What each part lists is a thing the application does today (the same claims as the boxes under it on the page);
 * copy here must not get ahead of the product. Premium parts say so in a sentence, never in a chip whose text would
 * be the bare word the plans use.
 *
 * Motion. When the wheel first scrolls into view the slices bloom outward from the job in turn. After that a light
 * walks around the slices, a dashed orbit turns round the task ring, and the job breathes. The light stops once
 * somebody has chosen a slice (the choice is the highlight then), pauses while the pointer is over the wheel, and all
 * of it stops while the wheel is off screen. Under `prefers-reduced-motion` nothing moves and the wheel is simply drawn.
 * Only `transform` and `opacity` change.
 *
 * Colour is named from the theme and never written as a hex, so the operator's theme and dark mode apply.
 */

// Geometry, in the 400 x 400 space the drawing is made in.
const SIZE = 400;
const C = SIZE / 2;
const JOB_R = 60;
const SLICE_IN = 68;
const SLICE_OUT = 154;
const RING_IN = 160;
const RING_OUT = 194;
const ORBIT_R = 177;
const GAP = 1.4;

const STEP = 1.8;
const START = 1.4;
const CYCLE = STEP * 8;

const polar = (r, deg) => {
  const a = ((deg - 90) * Math.PI) / 180;
  return [C + r * Math.cos(a), C + r * Math.sin(a)];
};

/** An annular sector between two radii and two angles (degrees, clockwise from the top). */
export const sectorPath = (inner, outer, from, to) => {
  const [x1, y1] = polar(outer, from);
  const [x2, y2] = polar(outer, to);
  const [x3, y3] = polar(inner, to);
  const [x4, y4] = polar(inner, from);
  const f = (n) => n.toFixed(1);
  return `M${f(x1)} ${f(y1)}A${outer} ${outer} 0 0 1 ${f(x2)} ${f(y2)}L${f(x3)} ${f(y3)}A${inner} ${inner} 0 0 0 ${f(x4)} ${f(y4)}Z`;
};

const toneOf = (theme, name) => (theme.palette[name] || theme.palette.primary).main;

/**
 * Everything the wheel says. `tone` groups the slices: what you build in yourself, and what you do to pursue the
 * job. Order is clockwise from the top and is also the order the light walks.
 */
export const JOB = {
  key: 'job',
  tone: 'success',
  icon: WorkOutlineIcon,
  title: 'Find the job',
  headline: 'Start with the job you want.',
  features: [
    `Browse openings posted on ${brand.name} and collected from company career pages.`,
    'Paste a link to track any job you find somewhere else.',
    'Every job you track keeps its place in one list.',
  ],
};

export const SLICES = [
  {
    key: 'profile', label: ['Profile'], tone: 'primary', icon: BadgeOutlinedIcon,
    title: 'Profile', headline: 'Be ready before you apply.',
    features: [
      `Write your resume in ${brand.name}, or upload the one you have.`,
      'Download it as a PDF whenever you need it.',
      'Publish a profile page only if you choose to.',
    ],
  },
  {
    key: 'matches', label: ['Matches'], tone: 'info', icon: AutoAwesomeIcon,
    title: 'Matches', headline: 'See how you fit a job.', premium: true,
    features: [
      'A score for how you match a job, the reasons, and the skills you show and the ones you are missing.',
      'Strengthen your resume for one job: your own words, reworded, never added to.',
      'Read your fit on a role fit radar that employers never see.',
    ],
  },
  {
    key: 'applications', label: ['Applications'], tone: 'info', icon: AssignmentTurnedInOutlinedIcon,
    title: 'Applications', headline: 'Know where every one stands.',
    features: [
      'Apply, and keep every application and its status in one place.',
      `Choose an interview time from the link when an employer offers times through ${brand.name}.`,
      `Read and sign an offer letter in your browser when an employer sends one through ${brand.name}.`,
    ],
  },
  {
    key: 'calendar', label: ['Calendar'], tone: 'info', icon: CalendarMonthOutlinedIcon,
    title: 'Calendar', headline: 'Keep your week straight.',
    features: [
      `Connect Google or Microsoft, or keep your events in ${brand.name}.`,
      `Meetings you book through ${brand.name} land on it.`,
      'See your week at a glance, with the tasks that fall due beside your events.',
    ],
  },
  {
    key: 'prep', label: ['Interview', 'prep'], tone: 'primary', icon: QuestionAnswerOutlinedIcon,
    title: 'Interview prep', headline: 'Practise before the real one.', premium: true,
    features: [
      'Rehearse an interview for a specific job.',
      'Read the feedback afterwards, so the next one goes better.',
    ],
  },
  {
    key: 'network', label: ['Network'], tone: 'info', icon: ContactsOutlinedIcon,
    title: 'Network', headline: 'The people who can help.',
    features: [
      'Keep the people you are building a career through.',
      'Note when you said you would speak to them next.',
      'Follow-ups that have come round are waiting on your dashboard.',
    ],
  },
  {
    key: 'events', label: ['Events'], tone: 'info', icon: EventOutlinedIcon,
    title: 'Events', headline: 'Go where the hiring is.',
    features: [
      'Career fairs, networking evenings and info sessions.',
      'Post one yourself, or save the ones you plan to go to.',
    ],
  },
  {
    key: 'upskill', label: ['Upskill'], tone: 'primary', icon: SchoolOutlinedIcon,
    title: 'Upskill', headline: 'Close the gap.',
    features: [
      'Course suggestions drawn from your resume and your career goals.',
      'Chosen only from a catalogue of real courses, never made up.',
    ],
  },
];

export const TASKS = {
  key: 'tasks',
  tone: 'warning',
  icon: ChecklistRtlIcon,
  title: `Tasks, notes and ${brand.name}`,
  headline: 'Something to do next, always.',
  features: [
    'Personal task lists for the week around your search, so nothing slips.',
    'Group tasks with people you invite by email: a study group, a job-search buddy.',
    'Notes in categories of your own, which you can share.',
    `Ask ${brand.name} to add a task, book a meeting or write up a note. Type it in chat, or with Premium, say it out loud.`,
  ],
};

const LEGEND = [
  { tone: 'primary', label: 'Build yourself' },
  { tone: 'info', label: 'Pursue the job' },
  { tone: 'warning', label: 'Tasks around it' },
];

const bloom = keyframes`
  from { opacity: 0; transform: scale(0.55); }
  to { opacity: 1; transform: scale(1); }
`;
// A slice is lit for its own window of the cycle and dark for the rest.
const lit = keyframes`
  0% { opacity: 0; }
  2% { opacity: 1; }
  ${((STEP * 0.78) / CYCLE * 100).toFixed(2)}% { opacity: 1; }
  ${((STEP * 0.98) / CYCLE * 100).toFixed(2)}% { opacity: 0; }
  100% { opacity: 0; }
`;
const orbit = keyframes`
  to { transform: rotate(360deg); }
`;
const breathe = keyframes`
  0%, 100% { opacity: 0.35; transform: scale(1); }
  50% { opacity: 0; transform: scale(1.18); }
`;

/** One press target: an SVG group that behaves as a button for the mouse, the keyboard and a screen reader. */
const Part = ({ id, label, selected, onSelect, children, ...rest }) => (
  <g
    role="button"
    tabIndex={0}
    aria-pressed={selected}
    aria-label={label}
    data-part={id}
    data-selected={selected ? 'true' : 'false'}
    onClick={() => onSelect(id)}
    onKeyDown={(event) => {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        onSelect(id);
      }
    }}
    style={{ cursor: 'pointer', outline: 'none' }}
    {...rest}
  >
    {children}
  </g>
);

const Detail = ({ part }) => {
  const Icon = part.icon;
  return (
    <Paper
      variant="outlined"
      data-testid="wheel-detail"
      data-part={part.key}
      sx={{
        p: { xs: 2, sm: 3 }, borderRadius: 3, height: '100%',
        borderTop: (theme) => `4px solid ${toneOf(theme, part.tone)}`,
        '@keyframes wheelDetailIn': { from: { opacity: 0, transform: 'translateY(8px)' }, to: { opacity: 1, transform: 'none' } },
        animation: 'wheelDetailIn 260ms ease-out both',
        '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
      }}
    >
      <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 1 }}>
        <Box
          sx={{
            width: 44, height: 44, borderRadius: 2.5, display: 'grid', placeItems: 'center', flexShrink: 0,
            color: (theme) => toneOf(theme, part.tone), bgcolor: (theme) => alpha(toneOf(theme, part.tone), 0.16),
          }}
        >
          <Icon />
        </Box>
        <Box sx={{ minWidth: 0 }}>
          <Typography component="p" variant="h6" sx={{ fontWeight: 800, lineHeight: 1.2 }}>{part.title}</Typography>
          <Typography variant="body2" color="text.secondary">{part.headline}</Typography>
        </Box>
      </Stack>
      <Box component="ul" sx={{ m: 0, mt: 1.5, pl: 2.5, '& li': { mb: 0.9, lineHeight: 1.55 } }}>
        {part.features.map((feature) => <li key={feature}><Typography variant="body2" component="span">{feature}</Typography></li>)}
      </Box>
      {part.premium && (
        <Chip size="small" variant="outlined" color="primary" label="Part of the paid plan" sx={{ mt: 1.5, fontWeight: 600 }} />
      )}
    </Paper>
  );
};

const Intro = () => (
  <Paper
    variant="outlined"
    data-testid="wheel-intro"
    sx={{
      p: { xs: 2, sm: 3 }, borderRadius: 3, height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'center',
      borderTop: (theme) => `4px solid ${toneOf(theme, 'success')}`,
    }}
  >
    <Typography component="p" variant="h6" sx={{ fontWeight: 800, lineHeight: 1.25 }}>
      Your job in the middle. Everything that helps you land it, around it.
    </Typography>
    <Typography color="text.secondary" sx={{ mt: 1, lineHeight: 1.6 }}>
      Press a slice to see what {brand.name} does for it. Start with the job, and the rest follows.
    </Typography>
    <Box sx={{ mt: 2 }}>
      <Button component={RouterLink} to="/signup" variant="contained" sx={{ fontWeight: 700, textTransform: 'none' }}>
        Start with a free account
      </Button>
    </Box>
  </Paper>
);

const CareerWheel = () => {
  const ref = useRef(null);
  const { seen, on } = useInView(ref);
  const [selected, setSelected] = useState(null);
  const parts = { [JOB.key]: JOB, [TASKS.key]: TASKS, ...Object.fromEntries(SLICES.map((slice) => [slice.key, slice])) };
  const part = selected ? parts[selected] : null;

  // The bloom runs once seen. The loops stop too while the wheel is off screen.
  const reveal = seen ? 'running' : 'paused';
  const loop = seen && on ? 'running' : 'paused';

  return (
    <Box
      ref={ref}
      component="section"
      aria-labelledby="career-wheel-heading"
      data-motion={seen ? 'playing' : 'waiting'}
      sx={{ mb: { xs: 3, md: 4 } }}
    >
      <Typography id="career-wheel-heading" component="p" variant="overline" color="text.secondary" sx={{ fontWeight: 700, letterSpacing: '0.1em' }}>
        The Career Wheel
      </Typography>

      <Box sx={{ display: 'grid', gap: { xs: 2, md: 4 }, alignItems: 'center', gridTemplateColumns: { xs: '1fr', md: 'minmax(0, 420px) minmax(0, 1fr)' } }}>
        <Box
          data-testid="wheel"
          data-selected={selected ? 'true' : 'false'}
          sx={{
            width: '100%', maxWidth: 420, mx: 'auto',
            '& svg': { display: 'block', width: '100%', height: 'auto', overflow: 'visible' },
            '& [data-bloom]': {
              transformBox: 'view-box', transformOrigin: '50% 50%',
              animation: `${bloom} 520ms cubic-bezier(0.2, 0.8, 0.2, 1) both`, animationPlayState: reveal,
            },
            '& [data-light]': {
              animation: `${lit} ${CYCLE}s linear infinite both`, animationPlayState: loop, opacity: 0, pointerEvents: 'none',
            },
            '& [data-orbit]': {
              transformBox: 'view-box', transformOrigin: '50% 50%', animation: `${orbit} 48s linear infinite`, animationPlayState: loop,
            },
            '& [data-breathe]': {
              transformBox: 'fill-box', transformOrigin: '50% 50%', animation: `${breathe} 3.2s ease-in-out infinite`, animationPlayState: loop,
              pointerEvents: 'none',
            },
            // Once a slice is chosen the choice is the highlight, and the walking light stands down.
            '&[data-selected="true"] [data-light]': { animation: 'none', opacity: 0 },
            '&:hover [data-light]': { animationPlayState: 'paused' },
            '& [data-part]:focus-visible [data-shape]': { strokeWidth: 3 },
            '& [data-part]:hover [data-shape]': { fillOpacity: 0.34 },
            '& [data-part][data-selected="true"] [data-shape]': { fillOpacity: 0.5, strokeWidth: 3 },
            '@media (prefers-reduced-motion: reduce)': { '& *': { animation: 'none !important', transition: 'none !important' } },
          }}
        >
          <svg viewBox={`0 0 ${SIZE} ${SIZE}`} role="group" aria-label="The Career Wheel: your job in the middle, with what helps you get it around it">
            <Box component="g" data-bloom style={{ animationDelay: '0s' }}>
              {/* One button, drawn as eight segments so the ring lines up with the eight slices it runs around. */}
              <Part id="tasks" label={`Tasks, notes and ${brand.name}`} selected={selected === 'tasks'} onSelect={setSelected}>
                {Array.from({ length: 8 }, (_, i) => (
                  <Box
                    key={`ring-${i}`}
                    component="path"
                    data-shape
                    d={sectorPath(RING_IN, RING_OUT, i * 45 - 22.5 + GAP, i * 45 + 22.5 - GAP)}
                    sx={{ fill: (theme) => toneOf(theme, 'warning'), fillOpacity: 0.16, stroke: (theme) => toneOf(theme, 'warning'), strokeWidth: 1, strokeOpacity: 0.55, transition: 'fill-opacity 160ms' }}
                  />
                ))}
              </Part>
              {[0, 180].map((deg) => {
                const [x, y] = polar((RING_IN + RING_OUT) / 2, deg);
                return (
                  <Box
                    key={deg}
                    component="text"
                    x={x}
                    y={y}
                    textAnchor="middle"
                    dominantBaseline="central"
                    sx={{ fontSize: 11, fontWeight: 700, fill: (theme) => theme.palette.text.primary, pointerEvents: 'none', letterSpacing: '0.04em' }}
                  >
                    Tasks
                  </Box>
                );
              })}
              <Box
                component="circle"
                data-orbit
                cx={C}
                cy={C}
                r={ORBIT_R}
                sx={{ fill: 'none', stroke: (theme) => toneOf(theme, 'warning'), strokeWidth: 1.5, strokeDasharray: '2 11', strokeLinecap: 'round', strokeOpacity: 0.9, pointerEvents: 'none' }}
              />
            </Box>

            {SLICES.map((slice, i) => {
              const from = i * 45 - 22.5 + GAP;
              const to = i * 45 + 22.5 - GAP;
              const [tx, ty] = polar(111, i * 45);
              const Icon = slice.icon;
              return (
                <Box key={slice.key} component="g" data-bloom style={{ animationDelay: `${0.12 + i * 0.09}s` }}>
                  <Part id={slice.key} label={slice.title} selected={selected === slice.key} onSelect={setSelected}>
                    <Box
                      component="path"
                      data-shape
                      d={sectorPath(SLICE_IN, SLICE_OUT, from, to)}
                      sx={{ fill: (theme) => toneOf(theme, slice.tone), fillOpacity: 0.2, stroke: (theme) => toneOf(theme, slice.tone), strokeWidth: 1.5, strokeOpacity: 0.8, transition: 'fill-opacity 160ms' }}
                    />
                    <Box
                      component="path"
                      data-light
                      d={sectorPath(SLICE_IN - 3, SLICE_OUT + 3, from, to)}
                      style={{ animationDelay: `${START + i * STEP}s` }}
                      sx={{ fill: (theme) => toneOf(theme, slice.tone), fillOpacity: 0.28, stroke: (theme) => toneOf(theme, slice.tone), strokeWidth: 3 }}
                    />
                    <Icon x={tx - 10} y={ty - (slice.label.length > 1 ? 24 : 20)} width={20} height={20} sx={{ width: 20, height: 20, color: (theme) => toneOf(theme, slice.tone), pointerEvents: 'none' }} />
                    {slice.label.map((word, line) => (
                      <Box
                        key={word}
                        component="text"
                        x={tx}
                        y={ty + 6 + line * 13}
                        textAnchor="middle"
                        dominantBaseline="central"
                        sx={{ fontSize: 12, fontWeight: 700, fill: (theme) => theme.palette.text.primary, pointerEvents: 'none' }}
                      >
                        {word}
                      </Box>
                    ))}
                  </Part>
                </Box>
              );
            })}

            <Box component="g" data-bloom style={{ animationDelay: '0.9s' }}>
              <Box component="circle" data-breathe cx={C} cy={C} r={JOB_R} sx={{ fill: (theme) => toneOf(theme, 'success'), fillOpacity: 0.35 }} />
              <Part id="job" label="Find the job" selected={selected === 'job'} onSelect={setSelected}>
                <Box
                  component="circle"
                  data-shape
                  cx={C}
                  cy={C}
                  r={JOB_R}
                  sx={{ fill: (theme) => toneOf(theme, 'success'), fillOpacity: 0.22, stroke: (theme) => toneOf(theme, 'success'), strokeWidth: 2, transition: 'fill-opacity 160ms' }}
                />
                <WorkOutlineIcon x={C - 13} y={C - 30} width={26} height={26} sx={{ width: 26, height: 26, color: (theme) => toneOf(theme, 'success'), pointerEvents: 'none' }} />
                <Box component="text" x={C} y={C + 14} textAnchor="middle" dominantBaseline="central" sx={{ fontSize: 18, fontWeight: 800, fill: (theme) => theme.palette.text.primary, pointerEvents: 'none' }}>
                  Job
                </Box>
              </Part>
            </Box>
          </svg>

          <Stack direction="row" spacing={1.5} useFlexGap flexWrap="wrap" justifyContent="center" sx={{ mt: 1.5 }} data-testid="wheel-legend">
            {LEGEND.map((entry) => (
              <Stack key={entry.label} direction="row" spacing={0.75} alignItems="center">
                <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: (theme) => toneOf(theme, entry.tone) }} />
                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>{entry.label}</Typography>
              </Stack>
            ))}
          </Stack>
        </Box>

        <Box aria-live="polite" sx={{ minWidth: 0 }}>
          {part ? <Detail part={part} /> : <Intro />}
        </Box>
      </Box>
    </Box>
  );
};

export default CareerWheel;
